// Forest Wisp — wander + shoot behavior (first new data-driven enemy).

import { registerBehavior } from './registry';
import type { Entity } from '../entity';
import type { World } from '../world';
import { G } from '../state';
import { S, TILE } from '../constants';
import { rand, RandomDir } from '../rng';
import { SpawnBullet } from '../demon';
import { PlayerDir, PlayerDist } from '../demon';
import { CreateGem } from '../demon';
import { getEnemyDef } from '../../data/loader';
import { removeEntity } from '../entity';
import { SND_Pos } from '../../audio/index';

const tr = Math.trunc;

function shootInterval(entity: Entity): number {
  const def = getEnemyDef('forest-wisp');
  const p = def?.behaviorParams?.shootInterval;
  return typeof p === 'number' ? p : 90;
}

function projectileSpeed(entity: Entity): number {
  const def = getEnemyDef('forest-wisp');
  const p = def?.behaviorParams?.projectileSpeed;
  return typeof p === 'number' ? p : 6;
}

registerBehavior({
  id: 'wander-shoot',
  onSpawn(_world, entity) {
    entity.data.tick = 0;
    entity.data.wanderDir = RandomDir();
    entity.data.floatPhase = rand() % 256;
  },
  onTick(world, entity, dt) {
    if (G.game_paused || G.map_enabled) return;
    if (entity.roomId !== G.player_room) return;

    const tick = ((entity.data.tick as number) ?? 0) + 1;
    entity.data.tick = tick;

    const phase = ((entity.data.floatPhase as number) ?? 0) + dt * 0.05;
    entity.data.floatPhase = phase;
    const wanderDir = (entity.data.wanderDir as number) ?? 0;
    const spd = S(entity.data.speed as number) || S(2);
    entity.pos.x += tr(Math.cos(wanderDir) * spd * 0.3);
    entity.pos.y += tr(Math.sin(wanderDir) * spd * 0.3) + tr(Math.sin(phase) * 0.5);

    if (tick % 120 === 0) {
      entity.data.wanderDir = RandomDir();
    }

    const dist = PlayerDist(entity.pos.x, entity.pos.y);
    if (dist < S(24)) {
      if (G.shield_hp > 0) {
        G.shield_hp--;
        SND_Pos('dat/a/shieldhit.wav', 50, 0);
      } else if (G.player_dying === 0) {
        G.player_hp--;
        G.player_dying = 1;
        SND_Pos('dat/a/hit.wav', 128, 0);
      }
    }

    if (tick % shootInterval(entity) === 0 && dist < S(400)) {
      const dir = PlayerDir(entity.pos.x, entity.pos.y);
      SpawnBullet(entity.pos.x, entity.pos.y, 0, dir, projectileSpeed(entity), 0);
    }
  },
  onHit(_world, entity, damage) {
    entity.hp -= damage;
    if (entity.hp <= 0) {
      entity.hp = 0;
    }
  },
  onDeath(world, entity) {
    const gems = (entity.data.gems as number) ?? 2;
    CreateGem(entity.pos.x, entity.pos.y, entity.roomId, gems);
    G.killed_enemies++;
    removeEntity(world.entities, entity.id);
    SND_Pos('dat/a/tone.wav', 80, 0);
  },
});
